import React, { useState, useEffect, useCallback, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { operatorApiFetch } from '@services/operator/apiService';
import '../FulfillmentDashboard/FulfillmentWall.css';
import './ExceptionWall.css';

const POLL_INTERVAL = 60_000;

const ExceptionWall = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [clock, setClock] = useState(new Date());
  const containerRef = useRef(null);
  const wakeLockRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await operatorApiFetch('exception_dashboard/exceptions');
      if (res.ok) {
        const json = await res.json();
        setData(json.data);
        setStale(false);
      } else {
        setStale(true);
      }
    } catch {
      setStale(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const timer = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [fetchData]);

  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Wake Lock
  useEffect(() => {
    const req = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch { /* ignore */ }
    };
    req();
    const onVis = () => { if (document.visibilityState === 'visible') req(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      wakeLockRef.current?.release();
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  const timeStr = clock.toLocaleTimeString('zh-TW', { hour12: false });
  const dateStr = `${String(clock.getMonth() + 1).padStart(2, '0')}/${String(clock.getDate()).padStart(2, '0')}`;

  // Overall signal: red if any critical, yellow if any warning/info, green if all ok
  const overallSignal = (() => {
    if (!data?.items) return 'green';
    if (data.items.some((i) => i.severity === 'critical')) return 'red';
    if (data.items.some((i) => i.severity === 'warning' || i.severity === 'info')) return 'yellow';
    return 'green';
  })();

  if (loading && !data) {
    return (
      <div className="fw-loading">
        <div className="fw-loading-spinner" />
        <div className="fw-loading-text">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="fw ew" ref={containerRef}>
      <div className="fw-header">
        <div className="fw-header-left">
          <span className="fw-header-logo">GoWarehouse</span>
          <span className="fw-header-sep" />
          <span className="fw-header-title">{t('operator.exception_dashboard.title')}</span>
          <span className="ew-zone-badge">{t('operator.exception_dashboard.zone')}</span>
        </div>
        <div className="fw-header-right">
          <span className={`fw-live fw-live--${overallSignal}`} />
          <span className="fw-clock">{timeStr}</span>
          <span className="fw-date">{dateStr}</span>
        </div>
      </div>

      <div className="ew-grid">
        {(data?.items || []).map((item) => (
          <div key={item.key} className={`ew-card ew-card--${item.severity}`}>
            <div className="ew-card-title">
              {t(`operator.exception_dashboard.${item.key}`)}
            </div>
            <div className={`ew-card-count${item.count === 0 ? ' ew-card-count--zero' : ''}`}>
              {item.count === 0 ? '–' : item.count}
            </div>
          </div>
        ))}
      </div>

      <div className="fw-footer">
        <div className="fw-footer-info">
          <span>
            {t('operator.exception_dashboard.last_updated')}{' '}
            {data?.generated_at
              ? new Date(data.generated_at).toLocaleTimeString('zh-TW', { hour12: false })
              : '--:--:--'}
          </span>
          <span className="fw-footer-sep">|</span>
          <span>{t('operator.exception_dashboard.polling_interval', { seconds: POLL_INTERVAL / 1000 })}</span>
          {stale && <span className="fw-stale">{t('operator.exception_dashboard.data_stale')}</span>}
        </div>
        <button className="fw-fs-btn" onClick={toggleFullscreen}>
          {t('operator.exception_dashboard.fullscreen')}
        </button>
      </div>
    </div>
  );
};

export default ExceptionWall;
